import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/esm/Refresh';
import ExpandAllIcon from '@mui/icons-material/esm/ExpandMore';
import CollapseAllIcon from '@mui/icons-material/esm/UnfoldLess';

import { PageHeader } from '../../components/layout';
import WBSTreeNode from '../../components/wbs/WBSTreeNode';
import wbsService, { WBSTreeNode as WBSTreeNodeType } from '../../services/api/wbsService';
import { RootState } from '../../store';

export default function WBSTreePage() {
  const navigate = useNavigate();
  const { activeProjectId } = useSelector((state: RootState) => state.projects);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [treeData, setTreeData] = useState<WBSTreeNodeType[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchTreeData = async () => {
    if (!activeProjectId) {
      setError('No project selected');
      setTreeData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await wbsService.getProjectTree(activeProjectId);
      setTreeData(data);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Failed to fetch WBS tree:', err);
      setError(err.response?.data?.message || 'Failed to load WBS tree');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTreeData();
  }, [activeProjectId]);

  const handleExpandAll = () => {
    const expandAllNodes = (nodes: WBSTreeNodeType[]): WBSTreeNodeType[] => {
      return nodes.map((node) => ({
        ...node,
        isExpanded: true,
        children: node.children ? expandAllNodes(node.children) : [],
      }));
    };
    setTreeData(expandAllNodes(treeData));
  };

  const handleCollapseAll = () => {
    const collapseAllNodes = (nodes: WBSTreeNodeType[]): WBSTreeNodeType[] => {
      return nodes.map((node) => ({
        ...node,
        isExpanded: false,
        children: node.children ? collapseAllNodes(node.children) : [],
      }));
    };
    setTreeData(collapseAllNodes(treeData));
  };

  const handleNodeClick = (node: WBSTreeNodeType) => {
    // Navigate to detail page
    navigate(`/wbs/${node.id}`);
  };

  const handleViewEVM = (node: WBSTreeNodeType) => {
    navigate(`/wbs/${node.id}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader title="Work Breakdown Structure" />
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Work Breakdown Structure"
        subtitle={`Last updated: ${lastRefresh.toLocaleString()}`}
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'WBS' }]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Expand All">
              <IconButton size="small" onClick={handleExpandAll}>
                <ExpandAllIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Collapse All">
              <IconButton size="small" onClick={handleCollapseAll}>
                <CollapseAllIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchTreeData}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      {/* WBS Tree */}
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden', boxShadow: 1 }}>
        {treeData.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Alert severity="info">
              No WBS elements found for the selected project.
            </Alert>
          </Box>
        ) : (
          treeData.map((node) => (
            <WBSTreeNode
              key={node.id}
              node={node}
              onClick={handleNodeClick}
              onViewEVM={handleViewEVM}
            />
          ))
        )}
      </Box>
    </Box>
  );
}
